import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  NotificationsNoneOutlined as OutlinedNotificationsIcon,
  ChatBubbleOutlineOutlined as ChatBubbleOutlineOutlinedIcon,
  StoreOutlined as StoreIconOutlined,
  CalendarTodayOutlined as CalendarIconOutlined,
  Person2Outlined as Person2IconOutlined,
  SettingsOutlined as SettingsIconOutlined,
  LogoutOutlined as LogoutIconOutlined,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSelector from '../LanguageSelector/LanguageSelector';

const AppBar = ({ open, onToggleSidebar }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, collaborator, businesses, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState('all');
  const [selectedDate, setSelectedDate] = useState('today');

  const openUserMenu = Boolean(anchorEl);

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigateToProfile = () => {
    handleUserMenuClose();
    navigate('/settings/my-profile');
  };

  const handleNavigateToSettings = () => {
    handleUserMenuClose();
    navigate('/settings');
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('common.goodMorning');
    if (hour < 18) return t('common.goodAfternoon');
    return t('common.goodEvening');
  };

  const getFormattedDate = () => {
    const date = new Date();
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString(t('common.locale'), options);
  };

  const getUserInitials = () => {
    if (user?.name) {
      const nameParts = user.name.trim().split(' ');
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
      }
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const networks = [
    { value: 'all', label: t('common.allNetworks') },
    ...(businesses && businesses.length > 0
      ? businesses.map((business) => ({
          value: business.uuid,
          label: business.name,
        }))
      : []),
  ];

  const dateFilters = [
    { value: 'today', label: t('common.today') },
    { value: 'yesterday', label: t('common.yesterday') },
    { value: 'last7days', label: t('common.last7Days') },
    { value: 'last30days', label: t('common.last30Days') },
    { value: 'custom', label: t('common.custom') },
  ];

  return (
    <MuiAppBar
      position="fixed"
      sx={{
        width: { md: open ? `calc(100% - 260px)` : `calc(100% - 72px)` },
        ml: { md: open ? '260px' : '72px' },
        zIndex: (theme) => theme.zIndex.drawer - 1,
        backgroundColor: 'background.default',
        color: 'text.primary',
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: (theme) =>
          theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
        {/* Left Section: Greeting */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={onToggleSidebar}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: 'text.primary',
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {getGreeting()}, {user?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getFormattedDate()}
            </Typography>
          </Box>
        </Box>

        {/* Right Section: Filters and Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* Network Select */}
          <FormControl
            size="small"
            sx={{
              minWidth: 140,
              display: { xs: 'none', md: 'block' },
            }}
          >
            <Select
              value={selectedNetwork}
              onChange={(e) => setSelectedNetwork(e.target.value)}
              startAdornment={<StoreIconOutlined sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />}
              sx={{
                '& .MuiSelect-select': {
                  py: 1,
                },
              }}
            >
              {networks.map((network) => (
                <MenuItem key={network.value} value={network.value}>
                  {network.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Select */}
          <FormControl
            size="small"
            disabled
            sx={{
              minWidth: 120,
              display: { xs: 'none', md: 'block' },
              opacity: 0.5,
            }}
          >
            <Select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              startAdornment={<CalendarIconOutlined sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />}
              sx={{
                '& .MuiSelect-select': {
                  py: 1,
                },
              }}
            >
              {dateFilters.map((filter) => (
                <MenuItem key={filter.value} value={filter.value}>
                  {filter.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Mail Icon */}
          <IconButton
            size="small"
            sx={{
              color: 'text.secondary',
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            <Badge badgeContent=" " variant="dot" color="error">
              <ChatBubbleOutlineOutlinedIcon />
            </Badge>
          </IconButton>

          {/* Notifications Icon */}
          <IconButton
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <Badge badgeContent=" " variant="dot" color="error">
              <OutlinedNotificationsIcon />
            </Badge>
          </IconButton>

          {/* Language Selector */}
          <LanguageSelector iconOnly />

          {/* User Avatar */}
          <IconButton
            onClick={handleUserMenuOpen}
            size="small"
            sx={{ ml: 1 }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </Avatar>
          </IconButton>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={openUserMenu}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.name || t('common.myAccount')}
              </Typography>
              {user?.name && (
                <Typography variant="caption" color="text.secondary">
                  {t('common.myAccount')}
                </Typography>
              )}
            </Box>
            <Divider />
            <MenuItem onClick={handleNavigateToProfile}>
              <ListItemIcon>
                <Person2IconOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.profile')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleNavigateToSettings}>
              <ListItemIcon>
                <SettingsIconOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.settings')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIconOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>
                <Typography color="error">{t('common.logout')}</Typography>
              </ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;
